import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { BookOpen, Award, CheckCircle, Sparkles, TrendingUp, Shield, Zap } from 'lucide-react';
export function PeptideMasteryDetailSection() {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.05
  });
  return <section className="relative py-12 sm:py-16 md:py-20 px-4 sm:px-6 bg-gradient-to-b from-black via-purple-950/20 to-black overflow-hidden" data-id="element-3606">
      {/* Mystical background effects - matching existing sections */}
      <div className="absolute inset-0 opacity-10" style={{
      backgroundImage: `radial-gradient(circle at 20% 50%, rgba(168, 85, 247, 0.3) 0%, transparent 50%),
                         radial-gradient(circle at 80% 50%, rgba(59, 130, 246, 0.3) 0%, transparent 50%)`
    }} data-id="element-3607" />

      {/* Glowing orbs */}
      <div className="absolute top-1/4 left-1/3 w-64 h-64 sm:w-96 sm:h-96 bg-purple-600/10 rounded-full blur-2xl sm:blur-3xl animate-pulse-slow" data-id="element-3608" />
      <div className="absolute bottom-1/4 right-1/3 w-64 h-64 sm:w-96 sm:h-96 bg-blue-600/10 rounded-full blur-2xl sm:blur-3xl animate-pulse-slow" style={{
      animationDelay: '1s'
    }} data-id="element-3609" />

      <div ref={ref} className="relative z-10 max-w-5xl mx-auto" data-id="element-3610">
        {/* Main Headline */}
        <h2 className="text-3xl sm:text-4xl md:text-6xl font-serif text-center mb-8 sm:mb-12 leading-tight text-stone-100" style={{
        fontFamily: 'Cinzel, serif',
        textShadow: '0 0 30px rgba(168, 85, 247, 0.4)'
      }} data-psi-pro-details-section data-id="element-3611">
          Here's Exactly What You're Getting When You Join{' '}
          <span className="bg-gradient-to-r from-purple-400 via-pink-400 to-purple-500 bg-clip-text text-transparent" style={{
          textShadow: '0 0 40px rgba(168, 85, 247, 0.6)'
        }} data-id="element-3612">
            PSI PRO
          </span>
          :
        </h2>

        <p className="text-lg sm:text-xl md:text-2xl text-stone-300 text-center mb-10 sm:mb-16" data-id="element-3613">
          Let me break down the{' '}
          <span className="text-amber-400 font-bold" data-id="element-3614">COMPLETE</span> package...
        </p>

        {/* PEPTIDE MASTERY 2.0 - MAIN SECTION */}
        <div className="relative mb-12 sm:mb-20" data-id="element-3615">
          <div className="absolute inset-0 bg-gradient-radial from-amber-600/20 via-transparent to-transparent blur-3xl" data-id="element-3616" />

          <div className="relative bg-gradient-to-br from-amber-950/60 via-stone-950/80 to-black border-4 border-amber-600/60 p-6 sm:p-10 md:p-12 rounded-lg shadow-[0_0_60px_rgba(217,119,6,0.5)]" data-id="element-3617">
            {/* Corner accents */}
            <div className="absolute top-0 left-0 w-8 sm:w-12 h-8 sm:h-12" data-id="element-3618">
              <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-amber-500 to-transparent" data-id="element-3619" />
              <div className="absolute top-0 left-0 w-1 h-full bg-gradient-to-b from-amber-500 to-transparent" data-id="element-3620" />
            </div>
            <div className="absolute top-0 right-0 w-8 sm:w-12 h-8 sm:h-12" data-id="element-3621">
              <div className="absolute top-0 right-0 w-full h-1 bg-gradient-to-l from-amber-500 to-transparent" data-id="element-3622" />
              <div className="absolute top-0 right-0 w-1 h-full bg-gradient-to-b from-amber-500 to-transparent" data-id="element-3623" />
            </div>
            <div className="absolute bottom-0 left-0 w-8 sm:w-12 h-8 sm:h-12" data-id="element-3624">
              <div className="absolute bottom-0 left-0 w-full h-1 bg-gradient-to-r from-amber-500 to-transparent" data-id="element-3625" />
              <div className="absolute bottom-0 left-0 w-1 h-full bg-gradient-to-t from-amber-500 to-transparent" data-id="element-3626" />
            </div>
            <div className="absolute bottom-0 right-0 w-8 sm:w-12 h-8 sm:h-12" data-id="element-3627">
              <div className="absolute bottom-0 right-0 w-full h-1 bg-gradient-to-l from-amber-500 to-transparent" data-id="element-3628" />
              <div className="absolute bottom-0 right-0 w-1 h-full bg-gradient-to-t from-amber-500 to-transparent" data-id="element-3629" />
            </div>

            {/* Icon */}
            <div className="flex justify-center mb-6 sm:mb-8" data-id="element-3630">
              <div className="relative" data-id="element-3631">
                <div className="absolute inset-0 bg-amber-500/40 blur-xl animate-pulse-slow" data-id="element-3632" />
                <CheckCircle className="relative w-12 h-12 sm:w-16 sm:h-16 text-amber-400 drop-shadow-[0_0_15px_rgba(251,191,36,0.8)]" data-id="element-3633" />
              </div>
            </div>

            {/* Title */}
            <div className="text-center mb-6 sm:mb-8" data-id="element-3634">
              <h3 className="text-2xl sm:text-4xl md:text-5xl font-serif font-bold text-amber-400 mb-4" style={{
              fontFamily: 'Cinzel, serif',
              textShadow: '0 0 40px rgba(217, 119, 6, 0.6)'
            }} data-id="element-3635">
                PEPTIDE MASTERY 2.0 — THE COMPLETE BIOLOGICAL OPTIMIZATION
                BLUEPRINT
              </h3>

              <div className="inline-block px-4 sm:px-6 py-2 sm:py-3 bg-green-600/30 border-2 border-green-500/50 rounded-lg mt-4" data-id="element-3636">
                <p className="text-green-400 font-bold text-lg sm:text-xl" data-id="element-3637">
                  Included FREE when you join PSI PRO.
                </p>
              </div>

              <p className="text-stone-400 text-base sm:text-lg mt-4" data-id="element-3638">
                (Normally $2,500 — pay-in-full only, no payment plans)
              </p>
            </div>

            {/* Description */}
            <div className="space-y-4 sm:space-y-6 text-base sm:text-lg text-stone-300 mb-8 sm:mb-12" data-id="element-3639">
              <p className="text-lg sm:text-xl text-stone-100 font-semibold text-center" data-id="element-3640">
                This is the most comprehensive, scientifically validated,
                in-depth peptide training ever created.
              </p>

              <div className="bg-amber-950/30 border-l-4 border-amber-500 p-4 sm:p-6 space-y-3" data-id="element-3641">
                <p className="text-stone-100" data-id="element-3642">
                  The same knowledge base used when{' '}
                  <span className="font-bold text-amber-400" data-id="element-3643">
                    $120,000/year private clients
                  </span>{' '}
                  come to Brenden with "impossible" cases.
                </p>
                <p className="text-stone-100" data-id="element-3644">
                  The same protocols implemented when{' '}
                  <span className="font-bold text-amber-400" data-id="element-3645">
                    Olympic athletes
                  </span>{' '}
                  need an edge.
                </p>
                <p className="text-stone-100" data-id="element-3646">
                  The same frameworks applied when{' '}
                  <span className="font-bold text-amber-400" data-id="element-3647">
                    doctors send him their toughest cases
                  </span>
                  .
                </p>
              </div>

              <p className="text-xl sm:text-2xl font-bold text-center text-amber-400 mt-6 sm:mt-8" data-id="element-3648">
                Here's what's inside:
              </p>
            </div>

            {/* 130+ PEPTIDES SECTION */}
            <div className="mb-10 sm:mb-16" data-id="element-3649">
              <div className="bg-gradient-to-br from-purple-950/40 to-black border-2 border-purple-500/50 p-6 sm:p-8 rounded-lg" data-id="element-3650">
                <div className="flex items-center gap-4 mb-4 sm:mb-6" data-id="element-3651">
                  <Sparkles className="w-8 h-8 sm:w-10 sm:h-10 text-purple-400" data-id="element-3652" />
                  <h4 className="text-2xl sm:text-3xl font-bold text-purple-400" data-id="element-3653">
                    130+ PEPTIDES DECODED AT THE MOLECULAR LEVEL
                  </h4>
                </div>

                <div className="space-y-4 text-base sm:text-lg text-stone-300" data-id="element-3654">
                  <p className="text-lg sm:text-xl text-stone-100 font-semibold" data-id="element-3655">
                    Complete modules on over 130 powerful peptides — including
                    rare ones most "experts" have never heard of (and never will
                    unless they go to the source like Brenden has).
                  </p>

                  <p className="text-amber-400 font-bold" data-id="element-3656">
                    This is the ONLY course in the world that truly covers all
                    of Khavinson's peptides.
                  </p>

                  <p className="text-stone-100 font-semibold" data-id="element-3657">
                    Not summaries. Not overviews.
                  </p>

                  <p className="text-lg sm:text-xl text-stone-100 font-bold mb-4" data-id="element-3658">
                    Deep mechanistic breakdowns of:
                  </p>

                  <div className="space-y-3 pl-4 sm:pl-6 border-l-2 border-purple-500/30 text-sm sm:text-base" data-id="element-3659">
                    <p className="flex items-start gap-3" data-id="element-3660">
                      <CheckCircle className="w-5 h-5 text-purple-400 flex-shrink-0 mt-1" data-id="element-3661" />
                      <span data-id="element-3662">
                        <span className="font-bold text-purple-400" data-id="element-3663">
                          Exact mechanisms of action
                        </span>{' '}
                        (what it actually does in your body at the cellular and
                        molecular level)
                      </span>
                    </p>
                    <p className="flex items-start gap-3" data-id="element-3664">
                      <CheckCircle className="w-5 h-5 text-purple-400 flex-shrink-0 mt-1" data-id="element-3665" />
                      <span data-id="element-3666">
                        <span className="font-bold text-purple-400" data-id="element-3667">
                          Optimal dosing protocols
                        </span>{' '}
                        (the doses that actually work, based on real research
                        and real results)
                      </span>
                    </p>
                    <p className="flex items-start gap-3" data-id="element-3668">
                      <CheckCircle className="w-5 h-5 text-purple-400 flex-shrink-0 mt-1" data-id="element-3669" />
                      <span data-id="element-3670">
                        <span className="font-bold text-purple-400" data-id="element-3671">
                          Synergistic combinations
                        </span>{' '}
                        (how to stack peptides for exponential rather than
                        additive effects)
                      </span>
                    </p>
                    <p className="flex items-start gap-3" data-id="element-3672">
                      <CheckCircle className="w-5 h-5 text-purple-400 flex-shrink-0 mt-1" data-id="element-3673" />
                      <span data-id="element-3674">
                        <span className="font-bold text-purple-400" data-id="element-3675">
                          Timing and absorption optimization
                        </span>{' '}
                        (when to dose for maximum bioavailability)
                      </span>
                    </p>
                    <p className="flex items-start gap-3" data-id="element-3676">
                      <CheckCircle className="w-5 h-5 text-purple-400 flex-shrink-0 mt-1" data-id="element-3677" />
                      <span data-id="element-3678">
                        <span className="font-bold text-purple-400" data-id="element-3679">
                          Safety considerations
                        </span>{' '}
                        (real risks based on actual science, not internet
                        fear-mongering)
                      </span>
                    </p>
                    <p className="flex items-start gap-3" data-id="element-3680">
                      <CheckCircle className="w-5 h-5 text-purple-400 flex-shrink-0 mt-1" data-id="element-3681" />
                      <span data-id="element-3682">
                        <span className="font-bold text-purple-400" data-id="element-3683">
                          Product type identification
                        </span>{' '}
                        (how to tell if you're buying Type 1, 2, or 3
                        bioregulators)
                      </span>
                    </p>
                    <p className="flex items-start gap-3" data-id="element-3684">
                      <CheckCircle className="w-5 h-5 text-purple-400 flex-shrink-0 mt-1" data-id="element-3685" />
                      <span data-id="element-3686">
                        <span className="font-bold text-purple-400" data-id="element-3687">
                          Where to source high-quality versions
                        </span>{' '}
                        (because knowing what to take is worthless if what
                        you're taking is bunk)
                      </span>
                    </p>
                  </div>

                  <div className="bg-purple-950/30 p-4 sm:p-6 rounded-lg mt-6" data-id="element-3688">
                    <p className="text-lg sm:text-xl text-stone-100 font-bold" data-id="element-3689">
                      This goes far beyond what you'll find in any other course,
                      book, or training program.
                    </p>
                    <p className="text-purple-400 font-semibold mt-2 text-sm sm:text-base" data-id="element-3690">
                      Because no other program was created by someone who's
                      actually gone to the source.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* 54 PROTOCOLS SECTION */}
            <div className="mb-10 sm:mb-16" data-id="element-3691">
              <div className="bg-gradient-to-br from-cyan-950/40 to-black border-2 border-cyan-500/50 p-6 sm:p-8 rounded-lg" data-id="element-3692">
                <div className="flex items-center gap-4 mb-4 sm:mb-6" data-id="element-3693">
                  <Zap className="w-8 h-8 sm:w-10 sm:h-10 text-cyan-400" data-id="element-3694" />
                  <h4 className="text-2xl sm:text-3xl font-bold text-cyan-400" data-id="element-3695">
                    54 BRENDEN HENRY PROTOCOLS
                  </h4>
                </div>

                <p className="text-xl sm:text-2xl text-cyan-300 font-bold mb-6" data-id="element-3696">
                  Precision-Engineered Synergistic Protocols That Create
                  "Impossible" Transformations
                </p>

                <div className="space-y-4 text-base sm:text-lg text-stone-300" data-id="element-3697">
                  <p className="text-lg sm:text-xl text-stone-100 font-semibold" data-id="element-3698">
                    This is where theory becomes transformation.
                  </p>

                  <p data-id="element-3699">
                    These aren't generic templates you could find with enough
                    Google searching.
                  </p>
                  <p data-id="element-3700">These aren't "here's what might work" suggestions.</p>

                  <p className="text-lg sm:text-xl text-cyan-400 font-bold" data-id="element-3701">
                    These are precision-engineered protocols Brenden has
                    developed through:
                  </p>

                  <div className="space-y-3 pl-4 sm:pl-6 border-l-2 border-cyan-500/30 text-sm sm:text-base" data-id="element-3702">
                    <p className="flex items-start gap-3" data-id="element-3703">
                      <span className="text-cyan-400 flex-shrink-0" data-id="element-3704">→</span>
                      <span data-id="element-3705">
                        Years of translating original Russian research
                      </span>
                    </p>
                    <p className="flex items-start gap-3" data-id="element-3706">
                      <span className="text-cyan-400 flex-shrink-0" data-id="element-3707">→</span>
                      <span data-id="element-3708">
                        Extensive experimentation with 1,500+ participants
                      </span>
                    </p>
                    <p className="flex items-start gap-3" data-id="element-3709">
                      <span className="text-cyan-400 flex-shrink-0" data-id="element-3710">→</span>
                      <span data-id="element-3711">
                        Real-world application with private $15,000−$120,000
                        transformation clients
                      </span>
                    </p>
                    <p className="flex items-start gap-3" data-id="element-3712">
                      <span className="text-cyan-400 flex-shrink-0" data-id="element-3713">→</span>
                      <span data-id="element-3714">
                        Deep mechanistic understanding of peptide synergies
                        nobody else knows
                      </span>
                    </p>
                  </div>

                  <div className="bg-cyan-950/30 p-4 sm:p-6 rounded-lg mt-6" data-id="element-3715">
                    <p className="text-lg sm:text-xl text-stone-100 font-bold mb-4" data-id="element-3716">
                      Each protocol shows you:
                    </p>
                    <div className="space-y-2 pl-4 text-sm sm:text-base" data-id="element-3717">
                      <p className="flex items-start gap-3" data-id="element-3718">
                        <CheckCircle className="w-5 h-5 text-cyan-400 flex-shrink-0 mt-1" data-id="element-3719" />
                        <span data-id="element-3720">Exactly what peptides to use</span>
                      </p>
                      <p className="flex items-start gap-3" data-id="element-3721">
                        <CheckCircle className="w-5 h-5 text-cyan-400 flex-shrink-0 mt-1" data-id="element-3722" />
                        <span data-id="element-3723">Precise dosing schedules</span>
                      </p>
                      <p className="flex items-start gap-3" data-id="element-3724">
                        <CheckCircle className="w-5 h-5 text-cyan-400 flex-shrink-0 mt-1" data-id="element-3725" />
                        <span data-id="element-3726">How to stack them for maximum synergy</span>
                      </p>
                      <p className="flex items-start gap-3" data-id="element-3727">
                        <CheckCircle className="w-5 h-5 text-cyan-400 flex-shrink-0 mt-1" data-id="element-3728" />
                        <span data-id="element-3729">Expected timeline for results</span>
                      </p>
                      <p className="flex items-start gap-3" data-id="element-3730">
                        <CheckCircle className="w-5 h-5 text-cyan-400 flex-shrink-0 mt-1" data-id="element-3731" />
                        <span data-id="element-3732">How to source everything you need</span>
                      </p>
                    </div>
                  </div>

                  {/* Protocol Categories */}
                  <div className="mt-8" data-id="element-3733">
                    <p className="text-xl sm:text-2xl font-bold text-cyan-400 mb-6 text-center" data-id="element-3734">
                      Here are all 54 protocols you're getting:
                    </p>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6" data-id="element-3735">
                      {/* REGENERATION & HEALING */}
                      <div className="bg-emerald-950/30 border border-emerald-500/30 p-4 sm:p-6 rounded-lg" data-id="element-3736">
                        <h5 className="text-lg sm:text-xl font-bold text-emerald-400 mb-4" data-id="element-3737">
                          REGENERATION & HEALING:
                        </h5>
                        <ul className="space-y-2 text-xs sm:text-sm text-stone-300" data-id="element-3738">
                          <li data-id="element-3739">• Liver Regeneration</li>
                          <li data-id="element-3740">• Kidney Repair</li>
                          <li data-id="element-3741">• Bone Healing</li>
                          <li data-id="element-3742">• Accelerated Healing</li>
                          <li data-id="element-3743">• Regenerate Spinal Discs</li>
                        </ul>
                      </div>

                      {/* NEUROLOGICAL & COGNITIVE */}
                      <div className="bg-purple-950/30 border border-purple-500/30 p-4 sm:p-6 rounded-lg" data-id="element-3744">
                        <h5 className="text-lg sm:text-xl font-bold text-purple-400 mb-4" data-id="element-3745">
                          NEUROLOGICAL & COGNITIVE:
                        </h5>
                        <ul className="space-y-2 text-xs sm:text-sm text-stone-300" data-id="element-3746">
                          <li data-id="element-3747">• Traumatic Brain Injury Recovery</li>
                          <li data-id="element-3748">• Stroke Recovery</li>
                          <li data-id="element-3749">• Alzheimer's Disease Regeneration</li>
                          <li data-id="element-3750">• Parkinson's Disease Regeneration</li>
                          <li data-id="element-3751">• Cognitive Decline / Brain Fog Solution</li>
                          <li data-id="element-3752">
                            • Improve Speed of Learning and Memory Retention
                          </li>
                          <li data-id="element-3753">• ADHD Calm Focus Protocol</li>
                        </ul>
                      </div>

                      {/* MENTAL HEALTH & NEUROCHEMISTRY */}
                      <div className="bg-blue-950/30 border border-blue-500/30 p-4 sm:p-6 rounded-lg" data-id="element-3754">
                        <h5 className="text-lg sm:text-xl font-bold text-blue-400 mb-4" data-id="element-3755">
                          MENTAL HEALTH & NEUROCHEMISTRY:
                        </h5>
                        <ul className="space-y-2 text-xs sm:text-sm text-stone-300" data-id="element-3756">
                          <li data-id="element-3757">• Anti-Anxiety</li>
                          <li data-id="element-3758">• Mood Enhancement and Depression Relief</li>
                          <li data-id="element-3759">• Anhedonia Fix & Pleasure Re-Sensitization</li>
                          <li data-id="element-3760">• Addiction Support Stack</li>
                        </ul>
                      </div>

                      {/* METABOLIC & ENDOCRINE */}
                      <div className="bg-amber-950/30 border border-amber-500/30 p-4 sm:p-6 rounded-lg" data-id="element-3761">
                        <h5 className="text-lg sm:text-xl font-bold text-amber-400 mb-4" data-id="element-3762">
                          METABOLIC & ENDOCRINE:
                        </h5>
                        <ul className="space-y-2 text-xs sm:text-sm text-stone-300" data-id="element-3763">
                          <li data-id="element-3764">• Diabetes Control</li>
                          <li data-id="element-3765">• Fix Hypothyroidism</li>
                          <li data-id="element-3766">• Male Hormone Optimization</li>
                          <li data-id="element-3767">• Female Hormone Optimization</li>
                          <li data-id="element-3768">• Low Energy and Chronic Fatigue Fix</li>
                          <li data-id="element-3769">
                            • Stubborn Fat Loss and Muscle Gain Enhancement
                          </li>
                        </ul>
                      </div>

                      {/* CARDIOVASCULAR & CIRCULATORY */}
                      <div className="bg-red-950/30 border border-red-500/30 p-4 sm:p-6 rounded-lg" data-id="element-3770">
                        <h5 className="text-lg sm:text-xl font-bold text-red-400 mb-4" data-id="element-3771">
                          CARDIOVASCULAR & CIRCULATORY:
                        </h5>
                        <ul className="space-y-2 text-xs sm:text-sm text-stone-300" data-id="element-3772">
                          <li data-id="element-3773">• Ultimate Heart Protocol</li>
                          <li data-id="element-3774">• Blood Vessel and Artery Support</li>
                          <li data-id="element-3775">• Anemia Fix</li>
                        </ul>
                      </div>

                      {/* IMMUNE & AUTOIMMUNE */}
                      <div className="bg-green-950/30 border border-green-500/30 p-4 sm:p-6 rounded-lg" data-id="element-3776">
                        <h5 className="text-lg sm:text-xl font-bold text-green-400 mb-4" data-id="element-3777">
                          IMMUNE & AUTOIMMUNE:
                        </h5>
                        <ul className="space-y-2 text-xs sm:text-sm text-stone-300" data-id="element-3778">
                          <li data-id="element-3779">• Immune System Fortification</li>
                          <li data-id="element-3780">• Autoimmune Support</li>
                          <li data-id="element-3781">• Cancer Recovery Support</li>
                        </ul>
                      </div>

                      {/* ANTI-AGING & LONGEVITY */}
                      <div className="bg-pink-950/30 border border-pink-500/30 p-4 sm:p-6 rounded-lg" data-id="element-3782">
                        <h5 className="text-lg sm:text-xl font-bold text-pink-400 mb-4" data-id="element-3783">
                          ANTI-AGING & LONGEVITY:
                        </h5>
                        <ul className="space-y-2 text-xs sm:text-sm text-stone-300" data-id="element-3784">
                          <li data-id="element-3785">• Biological Age Reversal</li>
                          <li data-id="element-3786">• Elderly Lifespan Stack</li>
                          <li data-id="element-3787">• Skin Rejuvenation</li>
                          <li data-id="element-3788">• Hair Growth and Health</li>
                        </ul>
                      </div>

                      {/* RESPIRATORY & ENVIRONMENTAL */}
                      <div className="bg-teal-950/30 border border-teal-500/30 p-4 sm:p-6 rounded-lg" data-id="element-3789">
                        <h5 className="text-lg sm:text-xl font-bold text-teal-400 mb-4" data-id="element-3790">
                          RESPIRATORY & ENVIRONMENTAL:
                        </h5>
                        <ul className="space-y-2 text-xs sm:text-sm text-stone-300" data-id="element-3791">
                          <li data-id="element-3792">• Lung Power</li>
                          <li data-id="element-3793">• Ultimate Cold Tolerance</li>
                          <li data-id="element-3794">• Ultimate Anti-Hypoxiant</li>
                          <li data-id="element-3795">• "Hold Your Breath for Longer" Protocol</li>
                          <li data-id="element-3796">• Radiation Resilience and Recovery Regimen</li>
                        </ul>
                      </div>

                      {/* PERFORMANCE & ATHLETICS */}
                      <div className="bg-orange-950/30 border border-orange-500/30 p-4 sm:p-6 rounded-lg" data-id="element-3797">
                        <h5 className="text-lg sm:text-xl font-bold text-orange-400 mb-4" data-id="element-3798">
                          PERFORMANCE & ATHLETICS:
                        </h5>
                        <ul className="space-y-2 text-xs sm:text-sm text-stone-300" data-id="element-3799">
                          <li data-id="element-3800">• Athletic Performance Enhancement</li>
                          <li data-id="element-3801">• Military Personnel Protocol</li>
                        </ul>
                      </div>

                      {/* PAIN & INFLAMMATION */}
                      <div className="bg-rose-950/30 border border-rose-500/30 p-4 sm:p-6 rounded-lg" data-id="element-3802">
                        <h5 className="text-lg sm:text-xl font-bold text-rose-400 mb-4" data-id="element-3803">
                          PAIN & INFLAMMATION:
                        </h5>
                        <ul className="space-y-2 text-xs sm:text-sm text-stone-300" data-id="element-3804">
                          <li data-id="element-3805">• Chronic Pain Management</li>
                          <li data-id="element-3806">• Neuropathy Support</li>
                          <li data-id="element-3807">• Chronic Prostatitis Cure</li>
                        </ul>
                      </div>

                      {/* SENSORY & RESTORATION */}
                      <div className="bg-indigo-950/30 border border-indigo-500/30 p-4 sm:p-6 rounded-lg" data-id="element-3808">
                        <h5 className="text-lg sm:text-xl font-bold text-indigo-400 mb-4" data-id="element-3809">
                          SENSORY & RESTORATION:
                        </h5>
                        <ul className="space-y-2 text-xs sm:text-sm text-stone-300" data-id="element-3810">
                          <li data-id="element-3811">• Eye Health and Vision Enhancement</li>
                          <li data-id="element-3812">• Reverse Hearing Loss</li>
                          <li data-id="element-3813">• Restore Olfaction</li>
                        </ul>
                      </div>

                      {/* DIGESTIVE & ORAL */}
                      <div className="bg-lime-950/30 border border-lime-500/30 p-4 sm:p-6 rounded-lg" data-id="element-3814">
                        <h5 className="text-lg sm:text-xl font-bold text-lime-400 mb-4" data-id="element-3815">
                          DIGESTIVE & ORAL:
                        </h5>
                        <ul className="space-y-2 text-xs sm:text-sm text-stone-300" data-id="element-3816">
                          <li data-id="element-3817">• Digestive Health</li>
                          <li data-id="element-3818">• Reverse Gum Disease and Periodontitis</li>
                        </ul>
                      </div>

                      {/* SLEEP & RECOVERY */}
                      <div className="bg-violet-950/30 border border-violet-500/30 p-4 sm:p-6 rounded-lg" data-id="element-3819">
                        <h5 className="text-lg sm:text-xl font-bold text-violet-400 mb-4" data-id="element-3820">
                          SLEEP & RECOVERY:
                        </h5>
                        <ul className="space-y-2 text-xs sm:text-sm text-stone-300" data-id="element-3821">
                          <li data-id="element-3822">• Sleep Optimization</li>
                        </ul>
                      </div>

                      {/* QUALITY OF LIFE */}
                      <div className="bg-fuchsia-950/30 border border-fuchsia-500/30 p-4 sm:p-6 rounded-lg" data-id="element-3823">
                        <h5 className="text-lg sm:text-xl font-bold text-fuchsia-400 mb-4" data-id="element-3824">
                          QUALITY OF LIFE:
                        </h5>
                        <ul className="space-y-2 text-xs sm:text-sm text-stone-300" data-id="element-3825">
                          <li data-id="element-3826">• Supercharged Libido Stack</li>
                          <li data-id="element-3827">• Enhanced Tanning Protocol</li>
                          <li data-id="element-3828">• Pregnancy Symptom Relief</li>
                        </ul>
                      </div>

                      {/* RARE CONDITIONS */}
                      <div className="bg-sky-950/30 border border-sky-500/30 p-4 sm:p-6 rounded-lg" data-id="element-3829">
                        <h5 className="text-lg sm:text-xl font-bold text-sky-400 mb-4" data-id="element-3830">
                          RARE CONDITIONS:
                        </h5>
                        <ul className="space-y-2 text-xs sm:text-sm text-stone-300" data-id="element-3831">
                          <li data-id="element-3832">• Huntington's Disease Recovery</li>
                          <li data-id="element-3833">
                            • Duchenne Muscular Dystrophy or Spinal-Bulbar
                            Muscular Atrophy Healing
                          </li>
                          <li data-id="element-3834">• Sarcopenia Recovery</li>
                        </ul>
                      </div>
                    </div>
                  </div>

                  <div className="bg-cyan-950/30 p-4 sm:p-6 rounded-lg mt-8" data-id="element-3835">
                    <p className="text-lg sm:text-xl text-stone-100 font-bold mb-2" data-id="element-3836">
                      Each one is a complete roadmap.
                    </p>
                    <p className="text-lg sm:text-xl text-stone-100 font-bold mb-2" data-id="element-3837">
                      Each one is based on years of research and real human
                      results.
                    </p>
                    <p className="text-lg sm:text-xl text-stone-100 font-bold" data-id="element-3838">
                      Each one gives you exactly what you need to implement it
                      successfully.
                    </p>
                    <p className="text-xl sm:text-2xl text-cyan-400 font-bold mt-6" data-id="element-3839">
                      Having ONE protocol that actually works for YOUR specific
                      situation is worth more than the entire investment.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* 1,193 SCIENTIFIC CITATIONS */}
            <div className="mb-10 sm:mb-16" data-id="element-3840">
              <div className="bg-gradient-to-br from-blue-950/40 to-black border-2 border-blue-500/50 p-6 sm:p-8 rounded-lg" data-id="element-3841">
                <div className="flex items-center gap-4 mb-4 sm:mb-6" data-id="element-3842">
                  <BookOpen className="w-8 h-8 sm:w-10 sm:h-10 text-blue-400" data-id="element-3843" />
                  <h4 className="text-2xl sm:text-3xl font-bold text-blue-400" data-id="element-3844">
                    BACKED WITH 1,193 SCIENTIFIC CITATIONS
                  </h4>
                </div>

                <div className="space-y-4 text-base sm:text-lg text-stone-300" data-id="element-3845">
                  <p className="text-lg sm:text-xl text-stone-100 font-semibold" data-id="element-3846">
                    Every claim is backed by peer-reviewed research.
                  </p>

                  <p data-id="element-3847">All available in the Master Citation List.</p>

                  <p className="text-blue-400 font-bold" data-id="element-3848">
                    We sell this citation list separately for $1,193 (one dollar
                    per citation).
                  </p>

                  <p className="text-lg sm:text-xl text-stone-100 font-bold" data-id="element-3849">
                    But it's included inside Peptide Mastery for free.
                  </p>

                  <div className="bg-blue-950/30 p-4 sm:p-6 rounded-lg mt-6" data-id="element-3850">
                    <p className="text-stone-100 mb-2" data-id="element-3851">
                      This isn't bro-science.
                    </p>
                    <p className="text-stone-100 mb-2" data-id="element-3852">
                      This isn't anecdotal evidence.
                    </p>
                    <p className="text-stone-100 mb-4" data-id="element-3853">
                      This isn't "I read on Reddit that..."
                    </p>
                    <p className="text-lg sm:text-xl text-blue-400 font-bold" data-id="element-3854">
                      This is decades of scientific validation, centralized and
                      simplified into the most valuable biological enhancement
                      resource in the world.
                    </p>
                  </div>

                  <div className="space-y-3 pl-4 sm:pl-6 border-l-2 border-blue-500/30 mt-6 text-sm sm:text-base" data-id="element-3855">
                    <p data-id="element-3856">
                      When Brenden makes a claim, it's backed by original
                      research.
                    </p>
                    <p data-id="element-3857">
                      When he recommends a protocol, it's validated by studies
                      most people don't even know exist.
                    </p>
                    <p data-id="element-3858">
                      When he states a mechanism, it's supported by
                      peer-reviewed science.
                    </p>
                  </div>

                  <p className="text-xl sm:text-2xl text-center text-stone-100 font-bold mt-6" data-id="element-3859">
                    You're not getting opinions.
                  </p>
                  <p className="text-xl sm:text-2xl text-center text-blue-400 font-bold" data-id="element-3860">
                    You're getting facts.
                  </p>
                </div>
              </div>
            </div>

            {/* MASS SPECTROMETRY VERIFICATION */}
            <div className="mb-10 sm:mb-16" data-id="element-3861">
              <div className="bg-gradient-to-br from-emerald-950/40 to-black border-2 border-emerald-500/50 p-6 sm:p-8 rounded-lg" data-id="element-3862">
                <div className="flex items-center gap-4 mb-4 sm:mb-6" data-id="element-3863">
                  <Shield className="w-8 h-8 sm:w-10 sm:h-10 text-emerald-400" data-id="element-3864" />
                  <h4 className="text-2xl sm:text-3xl font-bold text-emerald-400" data-id="element-3865">
                    MASS SPECTROMETRY VERIFICATION
                  </h4>
                </div>

                <div className="space-y-4 text-base sm:text-lg text-stone-300" data-id="element-3866">
                  <p className="text-lg sm:text-xl text-stone-100 font-semibold" data-id="element-3867">
                    We don't just tell you what to take.
                  </p>

                  <p className="text-lg sm:text-xl text-stone-100 font-semibold" data-id="element-3868">
                    We show you PROOF that what you're buying is actually what
                    the label claims.
                  </p>

                  <div className="bg-red-950/30 border-l-4 border-red-500 p-4 sm:p-6 my-6" data-id="element-3869">
                    <p className="text-lg sm:text-xl text-red-400 font-bold mb-4" data-id="element-3870">
                      Because here's a dirty secret of the peptide industry:
                    </p>
                    <div className="space-y-2 text-stone-300 text-sm sm:text-base" data-id="element-3871">
                      <p data-id="element-3872">Not all peptides are created equal.</p>
                      <p data-id="element-3873">Some vendors are selling underdosed products.</p>
                      <p data-id="element-3874">Some are selling completely bunk products.</p>
                      <p data-id="element-3875">Some are selling contaminated products.</p>
                    </div>
                  </div>

                  <p className="text-stone-100 font-semibold" data-id="element-3876">
                    And you'd never know unless you had access to mass
                    spectrometry testing.
                  </p>

                  <p className="text-lg sm:text-xl text-emerald-400 font-bold" data-id="element-3877">
                    Which you now do.
                  </p>

                  <div className="bg-emerald-950/30 p-4 sm:p-6 rounded-lg mt-6" data-id="element-3878">
                    <p className="text-stone-100 mb-2 text-sm sm:text-base" data-id="element-3879">
                      <span className="font-bold text-emerald-400" data-id="element-3880">
                        Included in the course:
                      </span>{' '}
                      Mass spec analysis of popular peptide products, so you can
                      see exactly what's real and what's fake.
                    </p>
                    <p className="text-lg sm:text-xl text-emerald-400 font-bold mt-4" data-id="element-3881">
                      This alone has saved our clients thousands of dollars in
                      wasted purchases.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* LIFETIME UPDATES */}
            <div className="mb-10 sm:mb-16" data-id="element-3882">
              <div className="bg-gradient-to-br from-purple-950/40 to-black border-2 border-purple-500/50 p-6 sm:p-8 rounded-lg" data-id="element-3883">
                <div className="flex items-center gap-4 mb-4 sm:mb-6" data-id="element-3884">
                  <TrendingUp className="w-8 h-8 sm:w-10 sm:h-10 text-purple-400" data-id="element-3885" />
                  <h4 className="text-2xl sm:text-3xl font-bold text-purple-400" data-id="element-3886">
                    LIFETIME UPDATES
                  </h4>
                </div>

                <div className="space-y-4 text-base sm:text-lg text-stone-300" data-id="element-3887">
                  <p className="text-stone-100" data-id="element-3888">
                    The course has already been updated multiple times since its
                    original release.
                  </p>

                  <p className="text-lg sm:text-xl text-purple-400 font-bold" data-id="element-3889">
                    It's now 10x bigger than when it launched.
                  </p>

                  <p className="text-lg sm:text-xl text-stone-100 font-bold" data-id="element-3890">
                    And you get access to EVERY future update, forever.
                  </p>

                  <div className="space-y-3 pl-4 sm:pl-6 border-l-2 border-purple-500/30 mt-6 text-sm sm:text-base" data-id="element-3891">
                    <p data-id="element-3892">As new research emerges...</p>
                    <p data-id="element-3893">
                      As Brenden refines protocols based on new client
                      results...
                    </p>
                    <p data-id="element-3894">
                      As the science advances and new peptides become
                      available...
                    </p>
                  </div>

                  <p className="text-xl sm:text-2xl text-center text-purple-400 font-bold mt-6" data-id="element-3895">
                    You stay at the forefront.
                  </p>
                </div>
              </div>
            </div>

            {/* 12-MONTH TRANSFORMATION JOURNEY */}
            <div className="mb-8 sm:mb-12" data-id="element-3896">
              <div className="bg-gradient-to-br from-amber-950/40 to-black border-2 border-amber-500/50 p-6 sm:p-8 rounded-lg" data-id="element-3897">
                <div className="flex items-center gap-4 mb-4 sm:mb-6" data-id="element-3898">
                  <Award className="w-8 h-8 sm:w-10 sm:h-10 text-amber-400" data-id="element-3899" />
                  <h4 className="text-2xl sm:text-3xl font-bold text-amber-400" data-id="element-3900">
                    STRUCTURED AS A 12-MONTH TRANSFORMATION JOURNEY
                  </h4>
                </div>

                <div className="space-y-4 text-base sm:text-lg text-stone-300" data-id="element-3901">
                  <p className="text-lg sm:text-xl text-stone-100 font-semibold" data-id="element-3902">
                    Peptide Mastery 2.0 is strategically sequenced as a 12-month
                    progression that takes you from foundational understanding
                    to complete mastery.
                  </p>

                  <p className="text-lg sm:text-xl text-amber-400 font-bold mb-4" data-id="element-3903">
                    Each month unlocks a new phase:
                  </p>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-3904">
                    <div className="bg-amber-950/30 p-4 rounded border border-amber-500/30" data-id="element-3905">
                      <p className="font-bold text-amber-400 mb-1" data-id="element-3906">Month 1:</p>
                      <p className="text-sm" data-id="element-3907">
                        Introduction & Basics + Mass Spectrometry Analysis
                      </p>
                    </div>
                    <div className="bg-amber-950/30 p-4 rounded border border-amber-500/30" data-id="element-3908">
                      <p className="font-bold text-amber-400 mb-1" data-id="element-3909">Month 2:</p>
                      <p className="text-sm" data-id="element-3910">
                        Sleep Optimization, Circadian Rhythm Repair & Stress
                        Adaptation
                      </p>
                    </div>
                    <div className="bg-amber-950/30 p-4 rounded border border-amber-500/30" data-id="element-3911">
                      <p className="font-bold text-amber-400 mb-1" data-id="element-3912">Month 3:</p>
                      <p className="text-sm" data-id="element-3913">
                        Gut Health, Digestive Repair & Detoxification
                      </p>
                    </div>
                    <div className="bg-amber-950/30 p-4 rounded border border-amber-500/30" data-id="element-3914">
                      <p className="font-bold text-amber-400 mb-1" data-id="element-3915">Month 4:</p>
                      <p className="text-sm" data-id="element-3916">
                        Brain Health, Cognitive Enhancement & Neuro-Regeneration
                      </p>
                    </div>
                    <div className="bg-amber-950/30 p-4 rounded border border-amber-500/30" data-id="element-3917">
                      <p className="font-bold text-amber-400 mb-1" data-id="element-3918">Month 5:</p>
                      <p className="text-sm" data-id="element-3919">
                        Cardiovascular Regeneration, Metabolic Health & Thyroid
                        Function
                      </p>
                    </div>
                    <div className="bg-amber-950/30 p-4 rounded border border-amber-500/30" data-id="element-3920">
                      <p className="font-bold text-amber-400 mb-1" data-id="element-3921">Month 6:</p>
                      <p className="text-sm" data-id="element-3922">
                        Joint, Bone & Cartilage Regeneration
                      </p>
                    </div>
                    <div className="bg-amber-950/30 p-4 rounded border border-amber-500/30" data-id="element-3923">
                      <p className="font-bold text-amber-400 mb-1" data-id="element-3924">Month 7:</p>
                      <p className="text-sm" data-id="element-3925">
                        Energy Production, Sports Performance & Endurance
                      </p>
                    </div>
                    <div className="bg-amber-950/30 p-4 rounded border border-amber-500/30" data-id="element-3926">
                      <p className="font-bold text-amber-400 mb-1" data-id="element-3927">Month 8:</p>
                      <p className="text-sm" data-id="element-3928">
                        Men's Hormonal Health, Vitality & Fertility
                      </p>
                    </div>
                    <div className="bg-amber-950/30 p-4 rounded border border-amber-500/30" data-id="element-3929">
                      <p className="font-bold text-amber-400 mb-1" data-id="element-3930">Month 9:</p>
                      <p className="text-sm" data-id="element-3931">
                        Women's Hormonal Balance, Thyroid & Reproductive Health
                      </p>
                    </div>
                    <div className="bg-amber-950/30 p-4 rounded border border-amber-500/30" data-id="element-3932">
                      <p className="font-bold text-amber-400 mb-1" data-id="element-3933">Month 10:</p>
                      <p className="text-sm" data-id="element-3934">
                        Injury Repair & Vision Restoration
                      </p>
                    </div>
                    <div className="bg-amber-950/30 p-4 rounded border border-amber-500/30" data-id="element-3935">
                      <p className="font-bold text-amber-400 mb-1" data-id="element-3936">Month 11:</p>
                      <p className="text-sm" data-id="element-3937">
                        Total Immune Defense, Respiratory Health & Infection
                        Control
                      </p>
                    </div>
                    <div className="bg-amber-950/30 p-4 rounded border border-amber-500/30" data-id="element-3938">
                      <p className="font-bold text-amber-400 mb-1" data-id="element-3939">Month 12:</p>
                      <p className="text-sm" data-id="element-3940">
                        Ultimate Longevity, Skin Rejuvenation & Detoxification
                      </p>
                    </div>
                  </div>

                  <div className="bg-amber-950/30 p-4 sm:p-6 rounded-lg mt-6" data-id="element-3941">
                    <p className="text-lg sm:text-xl text-stone-100 font-bold mb-4" data-id="element-3942">
                      This systematic approach ensures:
                    </p>
                    <div className="space-y-2 pl-4 text-sm sm:text-base" data-id="element-3943">
                      <p className="flex items-start gap-3" data-id="element-3944">
                        <span className="text-amber-400 flex-shrink-0" data-id="element-3945">→</span>
                        <span data-id="element-3946">You're not overwhelmed with information</span>
                      </p>
                      <p className="flex items-start gap-3" data-id="element-3947">
                        <span className="text-amber-400 flex-shrink-0" data-id="element-3948">→</span>
                        <span data-id="element-3949">
                          You build comprehensive understanding methodically
                        </span>
                      </p>
                      <p className="flex items-start gap-3" data-id="element-3950">
                        <span className="text-amber-400 flex-shrink-0" data-id="element-3951">→</span>
                        <span data-id="element-3952">
                          You can implement protocols as you learn them
                        </span>
                      </p>
                      <p className="flex items-start gap-3" data-id="element-3953">
                        <span className="text-amber-400 flex-shrink-0" data-id="element-3954">→</span>
                        <span data-id="element-3955">
                          You develop true mastery rather than surface knowledge
                        </span>
                      </p>
                    </div>
                  </div>

                  <div className="text-center mt-8" data-id="element-3956">
                    <p className="text-lg sm:text-xl text-stone-100 font-bold mb-2" data-id="element-3957">
                      By the end of 12 months, you won't just have random
                      knowledge.
                    </p>
                    <p className="text-xl sm:text-2xl text-amber-400 font-bold" data-id="element-3958">
                      You'll have systematic mastery of biological optimization.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Final Summary */}
            <div className="text-center space-y-4 sm:space-y-6 mt-8 sm:mt-12" data-id="element-3959">
              <p className="text-xl sm:text-2xl md:text-3xl font-bold text-stone-100" data-id="element-3960">
                Again, that's{' '}
                <span className="text-amber-400" data-id="element-3961">Peptide Mastery 2.0</span>.
              </p>
              <p className="text-lg sm:text-xl text-stone-300" data-id="element-3962">
                Normally{' '}
                <span className="text-red-400 font-bold line-through" data-id="element-3963">
                  $2,500
                </span>{' '}
                (pay-in-full only).
              </p>
              <p className="text-2xl sm:text-3xl font-bold text-green-400" data-id="element-3964">
                But when you join PSI PRO, you get it INCLUDED FREE.
              </p>
            </div>
          </div>
        </div>

        {/* CTA after value reveal */}
        <div className="text-center mt-10 sm:mt-14" data-id="element-3965">
          <button onClick={() => {
          const pricingSection = document.querySelector('[data-pricing-section]');
          if (pricingSection) {
            pricingSection.scrollIntoView({
              behavior: 'smooth',
              block: 'start'
            });
          }
        }} className="inline-block px-8 sm:px-12 py-4 sm:py-5 bg-gradient-to-r from-amber-600 to-amber-500 hover:from-amber-500 hover:to-amber-400 text-black font-bold text-lg sm:text-xl rounded-lg shadow-[0_0_30px_rgba(217,119,6,0.4)] hover:shadow-[0_0_50px_rgba(217,119,6,0.6)] hover:scale-105 transition-all duration-300 min-h-[44px]" data-id="element-3966">
            Join the Private Research Order Now
          </button>
        </div>

        {/* Transition to next section */}
        <div className="text-center mt-12 sm:mt-16" data-id="element-3967">
          <p className="text-2xl sm:text-3xl md:text-4xl font-bold text-stone-100 mb-4" data-id="element-3968">
            Now here's where this gets truly{' '}
            <span className="text-purple-400" data-id="element-3969">unprecedented</span>...
          </p>
        </div>
      </div>
    </section>;
}